import React, { useState, useRef, useCallback, useEffect } from "react";
import { DiffEditor } from "@monaco-editor/react";
import DiffViewToolbar from "./DiffViewToolbar";

const diffOptions = {
    minimap: { enabled: false },
    fontSize: 14,
    lineNumbers: "on",
    scrollBeyondLastLine: true,
    enableSplitViewResizing: true,
    originalEditable: false,
    glyphMargin: true,
    folding: true,
    renderIndicators: true,
    ignoreTrimWhitespace: false,
};

export default function DiffEditorView({
    originalCode,
    modifiedCode,
    language,
    versionLabel,
    onRevert,
    onClose,
    onModifiedChange,
    componentId,
    diffVersionId,
    readOnly = false,
}) {
    const [isInline, setIsInline] = useState(false);
    const [changes, setChanges] = useState([]);
    const [changeIndex, setChangeIndex] = useState(0);
    const diffEditorRef = useRef(null);
    const intervalRef = useRef(null);
    const initialModifiedRef = useRef(modifiedCode);

    // Reset state when switching to a different version
    useEffect(() => {
        setChanges([]);
        setChangeIndex(0);
        initialModifiedRef.current = modifiedCode;
    }, [componentId, diffVersionId]);

    const refreshChanges = useCallback((editor) => {
        const lineChanges = editor?.getLineChanges();
        setChanges(lineChanges || []);
    }, []);

    const handleMount = useCallback(
        (editor) => {
            // Clear any previous polling interval before setting up a new one
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            diffEditorRef.current = editor;
            const modifiedEditor = editor.getModifiedEditor();
            if (!readOnly) {
                modifiedEditor.updateOptions({
                    readOnly: false,
                    domReadOnly: false,
                });
                modifiedEditor.onDidChangeModelContent(() => {
                    const value = modifiedEditor.getValue();
                    initialModifiedRef.current = value;
                    onModifiedChange?.(value);
                });
            }
            const interval = setInterval(() => refreshChanges(editor), 500);
            intervalRef.current = interval;
            setTimeout(() => refreshChanges(editor), 300);
        },
        [onModifiedChange, refreshChanges]
    );

    // Clean up interval on unmount
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const navigateToChange = useCallback(
        (index) => {
            const editor = diffEditorRef.current;
            if (!editor || !changes.length) return;
            const clamped = Math.max(0, Math.min(changes.length - 1, index));
            const change = changes[clamped];
            if (change) {
                const line =
                    change.modifiedStartLineNumber ||
                    change.modifiedEndLineNumber;
                editor.getModifiedEditor().revealLineInCenter(line);
                setChangeIndex(clamped);
            }
        },
        [changes]
    );

    const handleRevert = useCallback(() => {
        const editor = diffEditorRef.current;
        if (editor) {
            editor.getModifiedEditor().setValue(originalCode);
        }
        onRevert?.();
    }, [originalCode, onRevert]);

    return (
        <div className="h-full flex flex-col">
            <DiffViewToolbar
                versionLabel={versionLabel}
                isInline={isInline}
                onToggleInline={() => setIsInline((v) => !v)}
                onPrevChange={() => navigateToChange(changeIndex - 1)}
                onNextChange={() => navigateToChange(changeIndex + 1)}
                onRevert={readOnly ? null : handleRevert}
                onClose={onClose}
                changeIndex={changeIndex}
                totalChanges={changes.length}
            />
            <div className="flex-1 overflow-hidden">
                <DiffEditor
                    key={`${componentId}-${diffVersionId}-${isInline}`}
                    height="100%"
                    language={language}
                    original={originalCode}
                    modified={initialModifiedRef.current}
                    options={{
                        ...diffOptions,
                        renderSideBySide: !isInline,
                    }}
                    theme="vs-light"
                    onMount={handleMount}
                />
            </div>
        </div>
    );
}
